import React, { useCallback, useEffect, useRef } from 'react';
import { Alert, Button, LinearProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Filters from '../Filters';
import { useJugadorEstadisticas } from '../../hooks/queries';
import { playerKey, playerMetric } from '../../utils/playerRanking';
import PlayerComparison from './PlayerComparison';

const FIELDS = ['temporada', 'temporadaNombre', 'federacion', 'federacionNombre', 'categoria',
  'categoriaNombre', 'competicion', 'competicionNombre', 'grupo', 'grupoNombre', 'minutosJuego'];
const SCOPE = ['temporada', 'competicion', 'grupo'];
const CONTEXT_PARAMS = ['compareContextA', 'compareContextB'];
const PLAYER_PARAMS = ['compareA', 'compareB'];
const cleanContext = value => Object.fromEntries(FIELDS.filter(key => value[key] !== undefined).map(key => [key, value[key]]));
function readContext(raw, fallback) {
  if (raw === null) return { value: cleanContext(fallback), invalid: false };
  try {
    const value = JSON.parse(raw);
    if (!value || Array.isArray(value) || typeof value !== 'object'
      || FIELDS.some(key => value[key] != null && !['number', 'string'].includes(typeof value[key]))) {
      return { value: {}, invalid: true };
    }
    return { value: cleanContext(value), invalid: false };
  } catch {
    return { value: {}, invalid: true };
  }
}

export default function PlayerComparisonWorkspace({ initialContext, minimum, profileUrl, profileState }) {
  const { t } = useTranslation('directories');
  const [params, setParams] = useSearchParams();
  const currentParams = useRef(params);
  useEffect(() => { currentParams.current = params; }, [params]);
  useEffect(() => {
    const next = new URLSearchParams(currentParams.current);
    const missing = CONTEXT_PARAMS.filter(key => !next.has(key));
    if (!missing.length) return;
    missing.forEach(key => next.set(key, JSON.stringify(cleanContext(initialContext))));
    currentParams.current = next;
    setParams(next, { replace: true });
  }, [params, initialContext, setParams]);
  const contexts = CONTEXT_PARAMS.map(key => readContext(params.get(key), initialContext));
  const ids = PLAYER_PARAMS.map(key => params.get(key) || '');
  const ready = contexts.map(context => !context.invalid && SCOPE.every(key => context.value[key]));
  const firstQuery = useJugadorEstadisticas(contexts[0].value.grupo, contexts[0].value.temporada, { enabled: Boolean(ready[0]) });
  const secondQuery = useJugadorEstadisticas(contexts[1].value.grupo, contexts[1].value.temporada, { enabled: Boolean(ready[1]) });
  const queries = [firstQuery, secondQuery];
  const changeContext = useCallback((slot, update) => {
    const nextParams = new URLSearchParams(currentParams.current);
    const parsed = readContext(nextParams.get(CONTEXT_PARAMS[slot]), initialContext);
    const previous = parsed.value;
    const next = cleanContext(typeof update === 'function' ? update(previous) : update);
    if (!parsed.invalid && JSON.stringify(previous) === JSON.stringify(next) && nextParams.has(CONTEXT_PARAMS[slot])) return;
    if (SCOPE.some(key => String(previous[key] || '') !== String(next[key] || ''))) nextParams.delete(PLAYER_PARAMS[slot]);
    nextParams.set(CONTEXT_PARAMS[slot], JSON.stringify(next));
    currentParams.current = nextParams;
    setParams(nextParams, { replace: true });
  }, [initialContext, setParams]);
  const changeFirstContext = useCallback(update => changeContext(0, update), [changeContext]);
  const changeSecondContext = useCallback(update => changeContext(1, update), [changeContext]);
  const changeIds = nextIds => {
    const nextParams = new URLSearchParams(currentParams.current);
    nextIds.forEach((id, slot) => id ? nextParams.set(PLAYER_PARAMS[slot], id) : nextParams.delete(PLAYER_PARAMS[slot]));
    currentParams.current = nextParams;
    setParams(nextParams, { replace: true });
  };
  const records = contexts.map(({ value }, slot) => !ready[slot] || queries[slot].isError ? [] : (queries[slot].data || [])
    .filter(player => [
      ['idTemporada', 'temporada'], ['idCompeticion', 'competicion'], ['idGrupo', 'grupo'],
    ].every(([field, key]) => player[field] == null || String(player[field]) === String(value[key])))
    .filter(player => playerKey(player) === ids[slot] || playerMetric(player, 'minutosJugados') >= minimum)
    .map(player => ({ ...player, idTemporada: player.idTemporada ?? value.temporada,
      idCompeticion: player.idCompeticion ?? value.competicion, idGrupo: player.idGrupo ?? value.grupo })));
  const controls = contexts.map((context, slot) => <React.Fragment key={slot}>
    {context.invalid && <Alert severity="warning" action={<Button onClick={() => changeContext(slot, {})}>
      {t('comparison.resetContext')}
    </Button>}>{t('comparison.invalidContext')}</Alert>}
    <Filters variant="comparison" value={context.value} onChange={slot === 0 ? changeFirstContext : changeSecondContext} />
  </React.Fragment>);
  const notices = contexts.map((context, slot) => <React.Fragment key={slot}>
    {ready[slot] && queries[slot].isLoading && <LinearProgress aria-label={t('comparison.loadingPlayers')} />}
    {ready[slot] && queries[slot].isError && <Alert severity="error" action={<Button onClick={() => queries[slot].refetch()}>
      {t('comparison.retry')}
    </Button>}>{t('comparison.loadError')}</Alert>}
    {ready[slot] && !queries[slot].isLoading && !queries[slot].isError && ids[slot]
      && !records[slot].some(player => playerKey(player) === ids[slot]) &&
      <Alert severity="warning" action={<Button onClick={() => changeIds(ids.map((id, index) => index === slot ? '' : id))}>
        {t('comparison.clearPlayer')}
      </Button>}>{t('comparison.unavailablePlayer')}</Alert>}
  </React.Fragment>);
  return <PlayerComparison playersBySlot={records} ids={ids} onChange={changeIds} minimum={minimum}
    profileUrl={profileUrl} profileState={profileState} contexts={contexts.map(context => context.value)}
    contextControls={controls} notices={notices}
    selectionDisabled={ready.map((isReady, slot) => !isReady || queries[slot].isLoading || queries[slot].isError)} />;
}
